/**
 * Admin — Accounts: every Estimator account (customers, retailer staff, test
 * identities). Search, see what each holds, and delete one: the local row,
 * its memberships and location grants go; what the person created stays and
 * is unlinked. Optionally the Keycloak user is deleted too, through the
 * estimator-backend service account.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, Loader2, Trash2, ChevronLeft, ChevronRight, AlertTriangle, KeyRound, ClipboardList, MessageSquare, Store, Pencil } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { fetchAccounts, deleteAccount, type AccountRow, type AccountsResponse, type DeleteAccountResult } from '../../services/adminAccountsApi';
import { AccountAccessModal } from './AccountAccessModal';

const fmt = (s: string | null) => (s ? s.replace('T', ' ').slice(0, 16) : '—');

export const AdminAccounts: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [data, setData] = useState<AccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<AccountRow | null>(null);
  const [alsoKeycloak, setAlsoKeycloak] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DeleteAccountResult | null>(null);
  const [accessFor, setAccessFor] = useState<AccountRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchAccounts({ search, page, perPage: 50 })); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load accounts'); }
    finally { setLoading(false); }
  }, [search, page]);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!target) return;
    setBusy(true); setError(null);
    try {
      const r = await deleteAccount(target.id, alsoKeycloak && !!data?.keycloakAdmin);
      setResult(r);
      setTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <div>
            <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><Users size={22} /> Accounts</h2>
            <p className="text-muted small mb-0">
              Every Estimator account: customers, retailer staff, test identities. Deleting one removes the account and what it
              <em> holds</em> (retailer memberships, locations); what the person <em>created</em> stays and is unlinked — their estimates
              become guest estimates still reachable by their links, their comments keep the author name.
              {data && !data.keycloakAdmin && <span className="text-warning-emphasis"> The estimator-backend service account is not configured, so Keycloak users must be deleted in the Keycloak console.</span>}
            </p>
          </div>
        </div>
        <form className="d-flex flex-wrap gap-2 align-items-center" onSubmit={e => { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); }}>
          <div className="input-group input-group-sm" style={{ maxWidth: 380 }}>
            <span className="input-group-text"><Search size={14} /></span>
            <input className="form-control" placeholder="Email, name, Keycloak id…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-sm btn-primary">Search</button>
          {search && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>Clear</button>}
          {data && <span className="text-muted small ms-auto">{data.pagination.total} account{data.pagination.total === 1 ? '' : 's'}</span>}
        </form>
      </div>

      {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
      {result && (
        <div className="alert alert-success py-2 small">
          Deleted <strong>{result.email}</strong>. Unlinked {result.unlinked.estimates ?? 0} estimate(s), {result.unlinked.comments ?? 0} comment(s); removed {result.unlinked.memberships ?? 0} membership(s).
          {' '}Keycloak: {result.keycloak === 'deleted' ? 'user deleted' : result.keycloak === 'skipped' ? 'left as is' : result.keycloak === 'no_keycloak_user' ? 'no Keycloak user on record' : result.keycloak === 'not_configured' ? 'not configured — delete in the Keycloak console' : `failed (${result.keycloakError ?? 'unknown'}) — delete in the Keycloak console`}.
          <button type="button" className="btn-close float-end" aria-label="Dismiss" onClick={() => setResult(null)} />
        </div>
      )}

      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 small">
            <thead className="table-tc-slate">
              <tr>
                <th>Account</th>
                <th>Sign-in</th>
                <th>Last signed in</th>
                <th>Retailer access</th>
                <th className="text-center text-white" title="Saved estimates"><ClipboardList size={14} /></th>
                <th className="text-center text-white" title="Comments"><MessageSquare size={14} /></th>
                <th className="text-end">Delete</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</td></tr>
              ) : !data || data.accounts.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted">No accounts{search ? ' match' : ' yet'}.</td></tr>
              ) : data.accounts.map(a => (
                <tr key={a.id} className={a.isActive ? '' : 'text-muted'}>
                  <td>
                    <div className="fw-semibold">{[a.firstName, a.lastName].filter(Boolean).join(' ') || <span className="text-muted fst-italic">no name</span>}{!a.isActive && <span className="badge text-bg-secondary ms-1">inactive</span>}</div>
                    <div className="text-muted">{a.email}</div>
                    {a.phone && <div className="text-muted" style={{ fontSize: '0.85em' }}>{a.phone}</div>}
                  </td>
                  <td>
                    <div>{a.authProvider}</div>
                    {a.keycloakSub ? <div className="text-muted font-monospace d-inline-flex align-items-center gap-1" style={{ fontSize: '0.8em' }} title={a.keycloakSub}><KeyRound size={10} /> {a.keycloakSub.slice(0, 8)}…</div> : <div className="text-muted" style={{ fontSize: '0.8em' }}>no Keycloak user</div>}
                  </td>
                  <td className="text-nowrap">
                    <div>{fmt(a.lastLoginAt)}</div>
                    <div className="text-muted" style={{ fontSize: '0.8em' }}>created {fmt(a.createdAt)}</div>
                  </td>
                  <td>
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1">
                        {a.memberships.length === 0 ? <span className="text-muted">—</span> : a.memberships.map((m, i) => (
                          <div key={i}>
                            <Link to={m.retailerId ? `/admin/retailers/${m.retailerId}?tab=people` : '/admin/retailers'}>{m.retailerName ?? 'no retailer yet'}</Link>
                            {' '}<span className={`badge ${m.status === 'approved' ? 'text-bg-success' : m.status === 'rejected' ? 'text-bg-secondary' : 'text-bg-warning'}`}>{m.status}</span>
                            {m.status === 'approved' && <span className="text-muted"> · {m.role === 'manager' ? 'Manager' : 'Associate'}{m.allLocations ? (m.includeChildren ? ', entire retailer incl. sub-retailers' : ', whole retailer') : ''}</span>}
                            {m.status === 'approved' && !m.allLocations && (
                              <div className="text-muted" style={{ fontSize: '0.85em' }}>{m.locations.length ? m.locations.map(l => l.name).join(', ') : <span className="text-danger">no access — its locations were moved; edit to fix</span>}</div>
                            )}
                          </div>
                        ))}
                        {a.manualLocationCount > 0 && <div className="text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: '0.85em' }}><Store size={11} /> attached by hand to {a.manualLocationCount} location(s)</div>}
                      </div>
                      {isSuperAdmin && (
                        <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1 flex-shrink-0" title="Set this account's retailer access: retailers, role, locations" onClick={() => setAccessFor(a)}>
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="text-center">{a.estimateCount}</td>
                  <td className="text-center">{a.commentCount}</td>
                  <td className="text-end">
                    {isSuperAdmin && (
                      <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" title="Delete this account" onClick={() => { setResult(null); setAlsoKeycloak(true); setTarget(a); }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top small">
            <span className="text-muted">Page {data.pagination.page} of {data.pagination.total_pages}</span>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
              <button type="button" className="btn btn-outline-secondary" disabled={page >= data.pagination.total_pages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {accessFor && <AccountAccessModal userId={accessFor.id} onClose={() => { setAccessFor(null); load(); }} onChanged={() => { /* refreshed on close */ }} />}

      {target && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2"><Trash2 size={18} className="text-danger" /> Delete account</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setTarget(null)} disabled={busy} />
              </div>
              <div className="modal-body">
                <p className="mb-2"><strong>{target.email}</strong>{[target.firstName, target.lastName].filter(Boolean).length ? ` — ${[target.firstName, target.lastName].filter(Boolean).join(' ')}` : ''}</p>
                <ul className="small mb-3">
                  <li>The account and its sign-in are removed from the Estimator.</li>
                  <li>{target.memberships.length} retailer membership(s) and their location grants are removed.</li>
                  <li>{target.estimateCount} saved estimate(s) stay, as guest estimates still reachable by their links.</li>
                  <li>{target.commentCount} comment(s) stay with the author's name.</li>
                </ul>
                {data?.keycloakAdmin ? (
                  <div className="form-check">
                    <input id="del-kc" type="checkbox" className="form-check-input" checked={alsoKeycloak} disabled={busy || !target.keycloakSub} onChange={e => setAlsoKeycloak(e.target.checked)} />
                    <label htmlFor="del-kc" className="form-check-label small">
                      Also delete the Keycloak user{!target.keycloakSub && <span className="text-muted"> (none on record)</span>}. Otherwise they can sign in again and a fresh account is created.
                    </label>
                  </div>
                ) : (
                  <div className="alert alert-warning py-2 small mb-0">The Keycloak user stays. Delete it in the Keycloak console, or they can sign in again and a fresh account is created.</div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setTarget(null)} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-danger d-inline-flex align-items-center gap-1" onClick={confirmDelete} disabled={busy}>
                  {busy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={16} />} Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounts;
